import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
  Inventory2 as PreparingIcon,
  FlightTakeoff as ShippedIcon,
  AirplaneTicket as InTransitIcon,
  DirectionsCar as OutForDeliveryIcon,
  Home as DeliveredIcon,
  ErrorOutline as FailedIcon,
  AssignmentReturn as ReturnedIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import type { DeliveryStatus } from '../../services/purchasesService';

type DeliveryManagerPopupProps = {
  open: boolean;
  onClose: () => void;
  purchaseId: string;
  productId: string;
  buyerId: string;
  sellerId: string;
  productName: string;
  currentStatus?: DeliveryStatus;
  currentTrackingNumber?: string | null;
  onSaved?: () => void;
};

const statusIcon = (s: DeliveryStatus) => {
  switch (s) {
    case 'pending':
      return <PendingIcon sx={{ fontSize: 18 }} />;
    case 'preparing':
      return <PreparingIcon sx={{ fontSize: 18 }} />;
    case 'shipped':
      return <ShippedIcon sx={{ fontSize: 18 }} />;
    case 'in_transit':
      return <InTransitIcon sx={{ fontSize: 18 }} />;
    case 'out_for_delivery':
      return <OutForDeliveryIcon sx={{ fontSize: 18 }} />;
    case 'delivered':
      return <DeliveredIcon sx={{ fontSize: 18 }} />;
    case 'failed':
      return <FailedIcon sx={{ fontSize: 18 }} />;
    case 'returned':
      return <ReturnedIcon sx={{ fontSize: 18 }} />;
    default:
      return <PendingIcon sx={{ fontSize: 18 }} />;
  }
};

const statusLabel = (s: DeliveryStatus) => {
  switch (s) {
    case 'pending':
      return 'Delivery pending';
    case 'preparing':
      return 'Preparing package';
    case 'shipped':
      return 'Shipped';
    case 'in_transit':
      return 'In transit';
    case 'out_for_delivery':
      return 'Out for delivery';
    case 'delivered':
      return 'Delivered';
    case 'failed':
      return 'Delivery failed';
    case 'returned':
      return 'Returned';
    default:
      return 'Delivery pending';
  }
};

const statusColor = (s: DeliveryStatus) => {
  switch (s) {
    case 'delivered':
      return 'success';
    case 'failed':
    case 'returned':
      return 'error';
    case 'shipped':
    case 'in_transit':
    case 'out_for_delivery':
    case 'preparing':
      return 'info';
    case 'pending':
    default:
      return 'warning';
  }
};

const ALL_STATUSES: DeliveryStatus[] = [
  'pending',
  'preparing',
  'shipped',
  'in_transit',
  'out_for_delivery',
  'delivered',
  'failed',
  'returned',
];

const DeliveryManagerPopup: React.FC<DeliveryManagerPopupProps> = ({
  open,
  onClose,
  purchaseId,
  productId,
  buyerId,
  sellerId,
  productName,
  currentStatus = 'pending',
  currentTrackingNumber,
  onSaved,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const [deliverySupported, setDeliverySupported] = React.useState<boolean | null>(null);
  const [status, setStatus] = React.useState<DeliveryStatus>(currentStatus);
  const [trackingNumber, setTrackingNumber] = React.useState<string>(String(currentTrackingNumber || '').trim());
  const [carrier, setCarrier] = React.useState<string>('');
  const [note, setNote] = React.useState<string>('');

  React.useEffect(() => {
    if (!open) return;
    setError(null);
    setStatus(currentStatus);
    setTrackingNumber(String(currentTrackingNumber || '').trim());
    setCarrier('');
    setNote('');

    const load = async () => {
      setLoading(true);
      try {
        // If the product has delivery options linked, seller should manage delivery.
        const { count, error: countErr } = await supabase
          .from('product_delivery_options')
          .select('*', { count: 'exact', head: true })
          .eq('product_id', productId);

        if (countErr) throw countErr;
        setDeliverySupported((count || 0) > 0);
      } catch (e: any) {
        // Best-effort; if we can't determine, allow UI (seller may still want to add tracking)
        setDeliverySupported(null);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [open, productId, currentStatus, currentTrackingNumber]);

  const buildStatusLabel = (s: DeliveryStatus) => {
    return statusLabel(s);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const prevStatus = currentStatus;
      const prevTracking = String(currentTrackingNumber || '').trim();

      // Always update purchases table (current snapshot)
      const { error: updErr } = await supabase
        .from('purchases')
        .update({
          delivery_status: status,
          tracking_number: trackingNumber ? trackingNumber : null,
        })
        .eq('id', purchaseId);

      if (updErr) throw updErr;

      // Also write to delivery_status table (history). If the migration isn't applied yet, ignore.
      try {
        const { error: histErr } = await supabase
          .from('delivery_status')
          .insert({
            purchase_id: purchaseId,
            product_id: productId,
            buyer_id: buyerId,
            seller_id: sellerId,
            status,
            tracking_number: trackingNumber ? trackingNumber : null,
            carrier: carrier ? carrier : null,
            note: note ? note : null,
          });
        if (histErr) {
          // best-effort: don't block seller flow
        }
      } catch {
        // best-effort
      }

      // Buyer notification (best-effort) - only when delivery status changed
      try {
        const statusChanged = String(prevStatus || '').trim() !== String(status || '').trim();
        const trackingChanged = String(prevTracking || '').trim() !== String(trackingNumber || '').trim();
        if (statusChanged || trackingChanged) {
          let buyerEmail = '';
          let buyerFirstName = '';
          let sellerName = '';

          // Prefer snapshots from purchases row to avoid RLS issues
          try {
            const { data: pRow } = await supabase
              .from('purchases')
              .select('buyer_email,buyer_name,seller_name')
              .eq('id', purchaseId)
              .maybeSingle();
            const be = String((pRow as any)?.buyer_email || '').trim();
            const bn = String((pRow as any)?.buyer_name || '').trim();
            const sn = String((pRow as any)?.seller_name || '').trim();
            if (be) buyerEmail = be;
            if (bn) buyerFirstName = bn.split(' ')[0] || bn;
            if (sn) sellerName = sn;
          } catch {
            // best-effort
          }

          try {
            // Fallback: attempt users table lookup (may be blocked by RLS)
            if (!buyerEmail || !sellerName) {
              const ids = [buyerId, sellerId].filter(Boolean);
              if (ids.length > 0) {
                const { data: users } = await supabase
                  .from('users')
                  .select('id,email,first_name,last_name,alias')
                  .in('id', ids);
                (users || []).forEach((u: any) => {
                  const id = String(u?.id || '').trim();
                  if (!id) return;
                  const email = String(u?.email || '').trim();
                  const first = String(u?.first_name || '').trim();
                  const last = String(u?.last_name || '').trim();
                  const alias = String(u?.alias || '').trim();
                  if (id === String(buyerId) && !buyerEmail) {
                    buyerEmail = email;
                    buyerFirstName = buyerFirstName || first || alias || 'there';
                  }
                  if (id === String(sellerId) && !sellerName) {
                    sellerName = [first, last].filter(Boolean).join(' ').trim() || alias || 'Seller';
                  }
                });
              }
            }
          } catch {
            // best-effort
          }

          if (buyerEmail) {
            const payload = {
              event: 'delivery_status_changed',
              user: {
                id: buyerId,
                email: buyerEmail,
                firstName: buyerFirstName,
              },
              seller: {
                id: sellerId,
                name: sellerName,
              },
              product: {
                id: productId,
                name: productName,
              },
              purchase: {
                id: purchaseId,
              },
              delivery: {
                status: status,
                previousStatus: prevStatus,
                statusLabel: buildStatusLabel(status),
                trackingNumber: trackingNumber ? trackingNumber : (prevTracking || ''),
                carrier: carrier,
                note: note,
              },
            };

            fetch('/api/notifications-dispatch.php', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
            })
              .then(async (res) => {
                const raw = await res.text().catch(() => '');
                let json: any = null;
                try {
                  json = raw ? JSON.parse(raw) : null;
                } catch {
                  json = null;
                }

                if (!res.ok || !json?.ok) {
                  console.error('delivery_status_changed dispatch failed', {
                    status: res.status,
                    statusText: res.statusText,
                    response: raw,
                    parsed: json,
                    payload,
                  });
                }
              })
              .catch((err) => {
                console.error('delivery_status_changed dispatch network error', err);
              });
          }
        }
      } catch {
        // best-effort
      }

      toast.success('Delivery updated');
      onSaved?.();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to update delivery');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(255,255,255,0.92) 100%)',
          backdropFilter: 'blur(18px)',
        },
      }}
    >
      <DialogTitle sx={{ position: 'relative', p: 0 }}>
        <Box
          sx={{
            px: 2.5,
            py: 2.25,
            background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 55%, #7c3aed 100%)',
            color: 'white',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2.5,
                background: 'rgba(255,255,255,0.16)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid rgba(255,255,255,0.22)',
              }}
            >
              <ShippingIcon sx={{ color: 'white' }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 950, lineHeight: 1.15 }}>
                Delivery manager
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }} noWrap>
                {productName}
              </Typography>
            </Box>
            <Chip
              icon={status === 'delivered' ? <CheckCircleIcon /> : statusIcon(status)}
              label={statusLabel(status)}
              color={statusColor(status) as any}
              size="small"
              sx={{
                fontWeight: 900,
                borderRadius: 999,
                bgcolor: 'rgba(255,255,255,0.18)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.24)',
                '& .MuiChip-icon': { color: 'white' },
              }}
            />
          </Box>
        </Box>

        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 10,
            top: 10,
            color: 'rgba(255,255,255,0.95)',
            bgcolor: 'rgba(255,255,255,0.12)',
            border: '1px solid rgba(255,255,255,0.18)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.18)' },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.25 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {deliverySupported === false && (
              <Alert severity="info" sx={{ mb: 2 }}>
                This product has no delivery option configured. You can still add tracking if you shipped it externally.
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.28 }}
              sx={{
                p: 2,
                borderRadius: 2.5,
                border: '1px solid rgba(0,0,0,0.06)',
                background:
                  'linear-gradient(135deg, rgba(14,165,233,0.08) 0%, rgba(37,99,235,0.06) 48%, rgba(124,58,237,0.06) 100%)',
              }}
            >
              <Typography sx={{ fontWeight: 950, mb: 0.5 }}>What’s happening next</Typography>
              <Typography variant="body2" color="text.secondary">
                Keep buyers confident by updating shipping progress and adding a tracking number as soon as you have one.
              </Typography>
            </Box>

            <Box sx={{ mt: 2.25, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.75 }}>
              <FormControl fullWidth>
                <InputLabel id="delivery-status-label">Delivery status</InputLabel>
                <Select
                  labelId="delivery-status-label"
                  label="Delivery status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as DeliveryStatus)}
                >
                  {ALL_STATUSES.map((s) => (
                    <MenuItem key={s} value={s}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {statusIcon(s)}
                        <span>{statusLabel(s)}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id="carrier-label">Carrier (optional)</InputLabel>
                <Select
                  labelId="carrier-label"
                  label="Carrier (optional)"
                  value={carrier}
                  onChange={(e) => setCarrier(String(e.target.value || ''))}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="DHL">DHL</MenuItem>
                  <MenuItem value="Hermes">Hermes</MenuItem>
                  <MenuItem value="DPD">DPD</MenuItem>
                  <MenuItem value="UPS">UPS</MenuItem>
                  <MenuItem value="FedEx">FedEx</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ mt: 1.75 }}>
              <TextField
                fullWidth
                label="Tracking number (optional)"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                placeholder="e.g. JD014600003838000000"
              />
            </Box>

            <Box sx={{ mt: 1.75 }}>
              <TextField
                fullWidth
                label="Message to buyer (optional)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Packed and shipping out tomorrow morning."
                multiline
                minRows={3}
              />
            </Box>

            <Divider sx={{ my: 2.25 }} />

            <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setStatus('preparing');
                }}
                sx={{ fontWeight: 900, borderRadius: 2 }}
              >
                Preparing
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setStatus('shipped');
                }}
                sx={{ fontWeight: 900, borderRadius: 2 }}
              >
                Mark shipped
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setStatus('delivered');
                }}
                sx={{ fontWeight: 900, borderRadius: 2 }}
              >
                Mark delivered
              </Button>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
        <Button onClick={onClose} variant="outlined" sx={{ fontWeight: 900, borderRadius: 2 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
          sx={{
            fontWeight: 950,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 55%, #7c3aed 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #0284c7 0%, #1d4ed8 55%, #6d28d9 100%)',
            },
          }}
        >
          Save update
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeliveryManagerPopup;
